import React from "react"
import cx from "classnames"
import { useNavigationContext } from "./navigation-context"
import { EXPANDED_WIDTH } from "./navigation-utils"
import { NavigationDockAction } from "./navigation-dock-action"
import { NavigationDockActions } from "./navigation-dock-actions"
import { NavigationDockPrimaryAction } from "./navigation-dock-primary-action"
import { NavigationDockTrigger } from "./navigation-dock-trigger"

export type { NavigationDockActionProps } from "./navigation-dock-action"
export type { NavigationDockActionsProps } from "./navigation-dock-actions"
export type { NavigationDockPrimaryActionProps } from "./navigation-dock-primary-action"
export type { NavigationDockTriggerProps } from "./navigation-dock-trigger"

// =============================================================================
// Props
// =============================================================================

export type NavigationDockProps = {
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const DOCK_Z_INDEX = 10

const dockStyles = [
  "fixed bottom-0 right-0 z-(--navigation-dock-z)",
  "flex items-center gap-4 p-4",
  "transition-[left] duration-[250ms] ease-out",
  "bg-gradient-to-b from-white/0 to-white",
  // Gradient area is non-interactive; children restore pointer events individually
  "pointer-events-none",
  // Hide action slots when the sidebar is expanded
  "[&[data-expanded]_[data-slot='navigation-dock-actions']]:hidden",
  "[&[data-expanded]_[data-slot='navigation-dock-primary-action']]:hidden"
].join(" ")

// =============================================================================
// Root
// =============================================================================

/**
 * A floating bottom navigation bar for mobile viewports.
 *
 * Compose with `Navigation.Dock.Trigger`, `Navigation.Dock.Actions` /
 * `Navigation.Dock.Action`, and `Navigation.Dock.PrimaryAction`.
 * Must be rendered inside `Navigation` (with the `mobile` prop) so that `Dock.Trigger`
 * shares collapsed state with the sidebar.
 *
 * @example
 * ```tsx
 * <Navigation mobile currentPath={currentPath}>
 *   <Navigation.Sidebar>...</Navigation.Sidebar>
 *   <Navigation.Dock>
 *     <Navigation.Dock.Trigger />
 *     <Navigation.Dock.Actions>
 *       <Navigation.Dock.Action label="Dashboard" icon="dashboard" pathPrefix="/dashboard" render={<Link href="/dashboard" />} />
 *     </Navigation.Dock.Actions>
 *     <Navigation.Dock.PrimaryAction label="Start writing" icon="pencil-write" render={<Link href="/campaigns/new" />} />
 *   </Navigation.Dock>
 * </Navigation>
 * ```
 */
function NavigationDockRoot({ children, className }: NavigationDockProps) {
  const { collapsed, mobile, setCollapsed } = useNavigationContext()

  if (!mobile) return null

  return (
    <>
      {!collapsed && (
        <div
          className="fixed inset-y-0 right-0"
          style={{ left: EXPANDED_WIDTH }}
          onClick={() => setCollapsed(true)}
          aria-hidden
        />
      )}
      <div
        data-slot="navigation-dock"
        className={cx(dockStyles, !collapsed ? "justify-start" : "justify-center", className)}
        data-expanded={!collapsed ? "" : undefined}
        style={{
          "--navigation-dock-z": DOCK_Z_INDEX,
          left: !collapsed ? EXPANDED_WIDTH : "0px",
        } as React.CSSProperties}
      >
        {children}
      </div>
    </>
  )
}

NavigationDockRoot.displayName = "Navigation.Dock"

// =============================================================================
// Compound component
// =============================================================================

interface NavigationDockComponent {
  (props: NavigationDockProps): React.JSX.Element | null
  Trigger: typeof NavigationDockTrigger
  Actions: typeof NavigationDockActions
  Action: typeof NavigationDockAction
  PrimaryAction: typeof NavigationDockPrimaryAction
}

export const NavigationDock: NavigationDockComponent = Object.assign(NavigationDockRoot, {
  Trigger: NavigationDockTrigger,
  Actions: NavigationDockActions,
  Action: NavigationDockAction,
  PrimaryAction: NavigationDockPrimaryAction
} as const)
